import React, { useState, useEffect } from 'react';
import { Tag, Loader2 } from 'lucide-react';
import Drawer from '@/components/ui/Drawer';
import {
  AttributionTag,
  AttributionTagDetail,
  AttributionTagStatus,
  CreateAttributionTagRequest,
  UpdateAttributionTagRequest,
  UnifiedHashtagDetail,
  getAvailableStatusOptions,
} from '@/types/attributionTag';
import { useAttributionTagStore } from '@/stores/attributionTagStore';

interface TagFormDrawerProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  tag?: AttributionTag | AttributionTagDetail | UnifiedHashtagDetail | null;
  onClose: () => void;
  onSuccess?: (tag: AttributionTag) => void;
}

interface FormData {
  tag_name: string;
  description: string;
  status: AttributionTagStatus;
}

interface FormErrors {
  tag_name?: string;
}

const TagFormDrawer: React.FC<TagFormDrawerProps> = ({
  isOpen,
  mode,
  tag,
  onClose,
  onSuccess,
}) => {
  const { createTag, updateTag, isSaving } = useAttributionTagStore();

  const [formData, setFormData] = useState<FormData>({
    tag_name: '',
    description: '',
    status: 'Active',
  });

  const [errors, setErrors] = useState<FormErrors>({});

  // Reset form when drawer opens or tag changes
  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && tag) {
        setFormData({
          tag_name: tag.hashtag_text || '',
          description: tag.description || '',
          status: tag.status || 'Active',
        });
      } else {
        setFormData({
          tag_name: '',
          description: '',
          status: 'Active',
        });
      }
      setErrors({});
    }
  }, [isOpen, mode, tag]);


  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Tag name validation
    if (!formData.tag_name.trim()) {
      newErrors.tag_name = 'Tag name is required';
    } else if (formData.tag_name.includes(' ')) {
      newErrors.tag_name = 'Tag name cannot contain spaces';
    } else if (formData.tag_name.length < 2 || formData.tag_name.length > 100) {
      newErrors.tag_name = 'Tag name must be 2-100 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    let result: AttributionTag | null = null;

    if (mode === 'create') {
      const createData: CreateAttributionTagRequest = {
        tag_name: formData.tag_name.trim(),
        description: formData.description.trim() || undefined,
        status: formData.status,
        account_ids: [], // Empty array - accounts are now associated with assets
      };
      result = await createTag(createData);
    } else if (tag) {
      const updateData: UpdateAttributionTagRequest = {
        description: formData.description.trim() || null,
        status: formData.status,
      };
      const tagId = String(tag.hashtag_id); // API uses hashtag_id
      result = await updateTag(tagId, updateData);
    }

    if (result) {
      onSuccess?.(result);
      onClose();
    }
  };

  const handleChange = (field: keyof FormData, value: string | string[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const availableStatuses = getAvailableStatusOptions(tag?.status ?? undefined);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'create' ? 'Create Attribution Tag' : 'Edit Attribution Tag'}
      description={
        mode === 'create'
          ? 'Create a new hashtag tag for commercial tracking'
          : `Editing #${tag?.hashtag_text || ''}`
      }
      icon={<Tag className="h-5 w-5" />}
      width="md"
    >
      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Tag Name */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Tag Name <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs">#</span>
            <input
              type="text"
              value={formData.tag_name}
              onChange={(e) => handleChange('tag_name', e.target.value.replace(/\s/g, ''))}
              placeholder="GoTigers"
              disabled={mode === 'edit' || isSaving}
              className={`w-full pl-5 pr-2 py-1.5 text-xs border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed ${
                errors.tag_name ? 'border-red-500' : 'border-gray-300'
              }`}
            />
          </div>
          {errors.tag_name && (
            <p className="mt-1 text-xs text-red-500">{errors.tag_name}</p>
          )}
          {mode === 'create' ? (
            <p className="mt-1 text-xs text-gray-500">
              The hashtag text without the # symbol. No spaces allowed.
            </p>
          ) : (
            <p className="mt-1 text-xs text-gray-500">
              Tag names cannot be changed after creation.
            </p>
          )}
        </div>

        {/* Description */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Official team hashtag for sponsor tracking"
            rows={3}
            disabled={isSaving}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed resize-none"
          />
        </div>

        {/* Status */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            value={formData.status}
            onChange={(e) => handleChange('status', e.target.value)}
            disabled={isSaving}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
          >
            {availableStatuses.map((statusOption) => (
              <option key={statusOption} value={statusOption}>
                {statusOption.charAt(0).toUpperCase() + statusOption.slice(1)}
              </option>
            ))}
          </select>
        </div>

        {/* Actions */}
        <div className="flex space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="flex-1 px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="flex-1 inline-flex items-center justify-center px-3 py-2 text-xs font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                {mode === 'create' ? 'Creating...' : 'Saving...'}
              </>
            ) : (
              mode === 'create' ? 'Create Tag' : 'Save Changes'
            )}
          </button>
        </div>
      </form>
    </Drawer>
  );
};

export default TagFormDrawer;
